import axios from "axios";
import { RbdManagementData } from "../../types/types";

interface ServiceResponse {
  success: boolean;
  message: string;
  data?: RbdManagementData;
}

const API_URL = process.env.REACT_APP_API_BASE_URL + "/rbdManagement";

// Helper: Formats baggage string (e.g., "1" -> "1 Piece" or "20" -> "20Kg")
const formatBaggage = (value: string, isKg: boolean): string => {
  // If value is null/undefined/empty string, return empty
  if (value === null || value === undefined || value.toString().trim() === "") {
    return "";
  }

  // 1. Clean the input: Remove existing "Piece(s)", "Piece", "Kg", etc. case-insensitive
  const cleanValue = value
    .toString()
    .replace(/Piece\(s\)|Pieces|Piece|Kg|kg|Pc|pc/gi, "")
    .trim();

  // 2. If the result is not a valid number, return empty
  if (cleanValue === "" || isNaN(Number(cleanValue))) return "";

  // 3. Append the correct suffix
  if (isKg) {
    return `${cleanValue}Kg`;
  } else {
    const num = parseInt(cleanValue);
    // FIXED: Only add 's' if number is greater than 1.
    // 0 -> "0 Piece", 1 -> "1 Piece", 2 -> "2 Pieces"
    return `${num} Piece${num > 1 ? "s" : ""}`;
  }
};

const handleError = (error: any): string => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Network error. Please check your internet connection.";
    }
    switch (error.response.status) {
      case 409:
        return "RBD combination already exists for this route and airline.";
      case 400:
        return error.response.data.message || "Invalid request data.";
      case 404:
        return "Resource not found.";
      case 500:
        return "Server error. Please try again later.";
      default:
        return (
          error.response.data.message ||
          `Error: ${error.response.status} - ${error.response.statusText}`
        );
    }
  }
  return "An unexpected error occurred";
};

const sortRbdData = (rbdData: RbdManagementData[]): RbdManagementData[] => {
  return rbdData.sort((a, b) => {
    const departureComparison = a.departure.localeCompare(b.departure);
    if (departureComparison !== 0) return departureComparison;
    const destinationComparison = a.destination.localeCompare(b.destination);
    if (destinationComparison !== 0) return destinationComparison;
    return a.airLine.localeCompare(b.airLine);
  });
};

const getRbd = async (): Promise<RbdManagementData[]> => {
  try {
    const response = await axios.get<RbdManagementData[]>(API_URL);
    return sortRbdData(response.data);
  } catch (error) {
    console.error(handleError(error));
    return [];
  }
};

const createRbd = async (
  rbd: Partial<RbdManagementData>
): Promise<ServiceResponse> => {
  try {
    const transformedRbd = {
      ...rbd,
      adultPrice: parseFloat(rbd.adultPrice?.toString() || "0"),
      childPrice: parseFloat(rbd.childPrice?.toString() || "0"),
      infantPrice: parseFloat(rbd.infantPrice?.toString() || "0"),

      outboundAdultBaggage: formatBaggage(
        rbd.outboundAdultBaggage || "",
        Boolean(rbd.isKgBaggage)
      ),
      outboundChildBaggage: formatBaggage(
        rbd.outboundChildBaggage || "",
        Boolean(rbd.isKgBaggage)
      ),
      outboundInfantBaggage: formatBaggage(
        rbd.outboundInfantBaggage || "",
        Boolean(rbd.isKgBaggage)
      ),
      inboundAdultBaggage: formatBaggage(
        rbd.inboundAdultBaggage || "",
        Boolean(rbd.isKgBaggage)
      ),
      inboundChildBaggage: formatBaggage(
        rbd.inboundChildBaggage || "",
        Boolean(rbd.isKgBaggage)
      ),
      inboundInfantBaggage: formatBaggage(
        rbd.inboundInfantBaggage || "",
        Boolean(rbd.isKgBaggage)
      ),

      isKgBaggage: Boolean(rbd.isKgBaggage),
      isOneWay: Boolean(rbd.isOneWay),
      isFlexible: Boolean(rbd.isFlexible),

      departureSurgeAdultPrice: parseFloat(
        rbd.departureSurgeAdultPrice?.toString() || "0"
      ),
      departureSurgeChildPrice: parseFloat(
        rbd.departureSurgeChildPrice?.toString() || "0"
      ),
      departureSurgeInfantPrice: parseFloat(
        rbd.departureSurgeInfantPrice?.toString() || "0"
      ),
      arrivalSurgeAdultPrice: parseFloat(
        rbd.arrivalSurgeAdultPrice?.toString() || "0"
      ),
      arrivalSurgeChildPrice: parseFloat(
        rbd.arrivalSurgeChildPrice?.toString() || "0"
      ),
      arrivalSurgeInfantPrice: parseFloat(
        rbd.arrivalSurgeInfantPrice?.toString() || "0"
      ),

      departureSurgeDays: rbd.departureSurgeDays || [],
      arrivalSurgeDays: rbd.arrivalSurgeDays || [],

      outboundValidity: rbd.outboundValidity,
      inboundValidity: rbd.inboundValidity,
      // REMOVED timeRange entirely
    };

    console.log("Sending RBD data:", JSON.stringify(transformedRbd, null, 2));

    const response = await axios.post<RbdManagementData>(
      API_URL,
      transformedRbd
    );

    return {
      success: true,
      message: "RBD details created successfully",
      data: response.data,
    };
  } catch (error) {
    console.error("Error creating RBD:", error);
    return { success: false, message: handleError(error) };
  }
};

const updateRbd = async (
  id: number,
  rbd: Partial<RbdManagementData>
): Promise<ServiceResponse> => {
  try {
    const transformedRbd: any = { ...rbd };

    if ("adultPrice" in rbd) transformedRbd.adultPrice = parseFloat(rbd.adultPrice?.toString() || "0");
    if ("childPrice" in rbd) transformedRbd.childPrice = parseFloat(rbd.childPrice?.toString() || "0");
    if ("infantPrice" in rbd) transformedRbd.infantPrice = parseFloat(rbd.infantPrice?.toString() || "0");

    if ("outboundAdultBaggage" in rbd) transformedRbd.outboundAdultBaggage = formatBaggage(rbd.outboundAdultBaggage || "", Boolean(rbd.isKgBaggage));
    if ("outboundChildBaggage" in rbd) transformedRbd.outboundChildBaggage = formatBaggage(rbd.outboundChildBaggage || "", Boolean(rbd.isKgBaggage));
    if ("outboundInfantBaggage" in rbd) transformedRbd.outboundInfantBaggage = formatBaggage(rbd.outboundInfantBaggage || "", Boolean(rbd.isKgBaggage));
    if ("inboundAdultBaggage" in rbd) transformedRbd.inboundAdultBaggage = formatBaggage(rbd.inboundAdultBaggage || "", Boolean(rbd.isKgBaggage));
    if ("inboundChildBaggage" in rbd) transformedRbd.inboundChildBaggage = formatBaggage(rbd.inboundChildBaggage || "", Boolean(rbd.isKgBaggage));
    if ("inboundInfantBaggage" in rbd) transformedRbd.inboundInfantBaggage = formatBaggage(rbd.inboundInfantBaggage || "", Boolean(rbd.isKgBaggage));

    if ("isKgBaggage" in rbd) transformedRbd.isKgBaggage = Boolean(rbd.isKgBaggage);
    if ("isOneWay" in rbd) transformedRbd.isOneWay = Boolean(rbd.isOneWay);
    if ("isFlexible" in rbd) transformedRbd.isFlexible = Boolean(rbd.isFlexible);

    if ("departureSurgeAdultPrice" in rbd) transformedRbd.departureSurgeAdultPrice = parseFloat(rbd.departureSurgeAdultPrice?.toString() || "0");
    if ("departureSurgeChildPrice" in rbd) transformedRbd.departureSurgeChildPrice = parseFloat(rbd.departureSurgeChildPrice?.toString() || "0");
    if ("departureSurgeInfantPrice" in rbd) transformedRbd.departureSurgeInfantPrice = parseFloat(rbd.departureSurgeInfantPrice?.toString() || "0");

    if ("arrivalSurgeAdultPrice" in rbd) transformedRbd.arrivalSurgeAdultPrice = parseFloat(rbd.arrivalSurgeAdultPrice?.toString() || "0");
    if ("arrivalSurgeChildPrice" in rbd) transformedRbd.arrivalSurgeChildPrice = parseFloat(rbd.arrivalSurgeChildPrice?.toString() || "0");
    if ("arrivalSurgeInfantPrice" in rbd) transformedRbd.arrivalSurgeInfantPrice = parseFloat(rbd.arrivalSurgeInfantPrice?.toString() || "0");

    console.log("Updating RBD data:", JSON.stringify(transformedRbd, null, 2));

    const response = await axios.patch(`${API_URL}/${id}`, transformedRbd);
    return {
      success: true,
      message: "RBD details updated successfully",
      data: response.data,
    };
  } catch (error) {
    console.error("Error updating RBD:", error);
    return { success: false, message: handleError(error) };
  }
};

const deleteRbd = async (id: number): Promise<ServiceResponse> => {
  try {
    await axios.delete(`${API_URL}/${id}`);
    return { success: true, message: "RBD details deleted successfully" };
  } catch (error) {
    return { success: false, message: handleError(error) };
  }
};

export { getRbd, createRbd, updateRbd, deleteRbd };


export const toggleSeoPin = async (id: number) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.patch(
      `${API_URL}/${id}/seo-pin`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
